import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/config/api";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, Calendar, DollarSign, Search } from "lucide-react";
import { PAYMENT_MODES } from "@/utils/constants";
import { TablePagination } from "@/components/TablePagination";
import { addDaysToLocalDateInputValue, toLocalDateInputValue } from "@/utils/date";

export default function Payments() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const role = String(user?.role || "").toLowerCase();
  const tomorrowDate = addDaysToLocalDateInputValue(1);
  const canRecordPayment = ["admin", "finance"].includes(role);
  const canRecordRefund = ["admin", "finance"].includes(role);
  const canRecordVendorSettlement = ["admin", "finance"].includes(role);
  const showTicketAction = role === "management" || role === "sales";
  const [payments, setPayments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [refundInvoices, setRefundInvoices] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [vendorPayments, setVendorPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [showRefundForm, setShowRefundForm] = useState(false);
  const [showVendorPaymentForm, setShowVendorPaymentForm] = useState(false);
  const [showVendorRefundForm, setShowVendorRefundForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [recordsTab, setRecordsTab] = useState("customer");
  const [paymentsView, setPaymentsView] = useState("all");
  const [vendorSettlementsView, setVendorSettlementsView] = useState("all");
  const [paymentsPage, setPaymentsPage] = useState(1);
  const [vendorSettlementsPage, setVendorSettlementsPage] = useState(1);
  const [tablePageSize, setTablePageSize] = useState(25);
  const [refundInvoiceQuery, setRefundInvoiceQuery] = useState("");
  const [paymentInvoiceQuery, setPaymentInvoiceQuery] = useState("");
  const [vendorPaymentQuery, setVendorPaymentQuery] = useState("");
  const [vendorRefundQuery, setVendorRefundQuery] = useState("");
  const [refundAllocations, setRefundAllocations] = useState([]);
  const [loadingRefundPreview, setLoadingRefundPreview] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(() => {
    try {
      const saved = localStorage.getItem("payments.visibleColumns");
      return saved
        ? JSON.parse(saved)
        : {
            invoiceAmount: true,
            paidAmount: true,
            balance: true,
          };
    } catch {
      return { invoiceAmount: true, paidAmount: true, balance: true };
    }
  });

  useEffect(() => {
    localStorage.setItem(
      "payments.visibleColumns",
      JSON.stringify(visibleColumns),
    );
  }, [visibleColumns]);

  const filteredPayments = useMemo(() => {
    const now = new Date();
    const recentCutoff = new Date(now);
    recentCutoff.setDate(now.getDate() - 30);

    let base = payments;
    if (paymentsView === "recent_refunds") {
      base = payments.filter((p) => {
        const isRefund = (p.transaction_type || "payment") === "refund";
        if (!isRefund) return false;
        const dateValue = p.payment_date ? new Date(p.payment_date) : null;
        return dateValue && !Number.isNaN(dateValue.getTime()) && dateValue >= recentCutoff;
      });
    }

    const s = (search || "").toLowerCase().trim();
    if (!s) return base;
    return base.filter(
      (p) =>
        (p.invoice_number || "").toLowerCase().includes(s) ||
        (p.customer_name || "").toLowerCase().includes(s) ||
        (p.reference_number || "").toLowerCase().includes(s),
    );
  }, [payments, search, paymentsView]);

  const filteredVendorPayments = useMemo(() => {
    let list = vendorPayments;
    if (vendorSettlementsView === "payments") {
      list = list.filter((payment) => Number(payment.amount || 0) >= 0);
    } else if (vendorSettlementsView === "refunds") {
      list = list.filter((payment) => Number(payment.amount || 0) < 0);
    }

    const s = (search || "").toLowerCase().trim();
    if (!s) return list;
    return list.filter(
      (payment) =>
        (payment.vendor_name || "").toLowerCase().includes(s) ||
        (payment.mode_of_payment || "").toLowerCase().includes(s) ||
        (payment.reference_number || "").toLowerCase().includes(s) ||
        (payment.notes || "").toLowerCase().includes(s),
    );
  }, [vendorPayments, vendorSettlementsView, search]);

  const paymentsTotalPages = Math.max(
    1,
    Math.ceil(filteredPayments.length / tablePageSize),
  );
  const vendorSettlementsTotalPages = Math.max(
    1,
    Math.ceil(filteredVendorPayments.length / tablePageSize),
  );
  const paginatedPayments = filteredPayments.slice(
    (paymentsPage - 1) * tablePageSize,
    paymentsPage * tablePageSize,
  );
  const paginatedVendorPayments = filteredVendorPayments.slice(
    (vendorSettlementsPage - 1) * tablePageSize,
    vendorSettlementsPage * tablePageSize,
  );

  useEffect(() => {
    setPaymentsPage(1);
    setVendorSettlementsPage(1);
  }, [search, paymentsView, vendorSettlementsView, recordsTab, tablePageSize]);

  const [formData, setFormData] = useState({
    invoice_id: "",
    amount: "",
    payment_date: toLocalDateInputValue(),
    notes: "",
    mode_of_payment: "CASH",
    reference_number: "",
  });
  const [refundForm, setRefundForm] = useState({
    invoice_id: "",
    reference_number: "",
    amount: "",
    refund_date: toLocalDateInputValue(),
    notes: "",
    mode_of_payment: "BANK_TRANSFER",
  });
  const [vendorPaymentForm, setVendorPaymentForm] = useState({
    vendor_id: "",
    amount: "",
    payment_date: toLocalDateInputValue(),
    mode_of_payment: "BANK_TRANSFER",
    reference_number: "",
    notes: "",
    currency: "OMR",
  });
  const [vendorRefundForm, setVendorRefundForm] = useState({
    vendor_id: "",
    amount: "",
    payment_date: toLocalDateInputValue(),
    mode_of_payment: "BANK_TRANSFER",
    reference_number: "",
    notes: "",
    currency: "OMR",
  });

  const selectedVendorForPayment = vendors.find(
    (vendor) => vendor.id === vendorPaymentForm.vendor_id,
  );
  const selectedVendorDue = parseFloat(
    selectedVendorForPayment?.payable_balance || 0,
  );
  const selectedVendorForRefund = vendors.find(
    (vendor) => vendor.id === vendorRefundForm.vendor_id,
  );
  const selectedVendorCredit = parseFloat(
    selectedVendorForRefund?.vendor_credit_balance || 0,
  );

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [paymentsData, invoicesData, vendorsData, vendorPaymentsData] = await Promise.all([
        api.payments.getAll(),
        api.invoices.getAll(),
        api.vendors.getAll().catch(() => []),
        api.payments.getVendorPayments().catch(() => []),
      ]);
      setPayments(paymentsData);
      setVendors(vendorsData);
      setVendorPayments(vendorPaymentsData);
      // Filter for approved invoices that are not fully paid
      const approvedInvoices = invoicesData.filter(
        (inv) => inv.status === "approved" && inv.payment_status !== "paid"
      );
      setInvoices(approvedInvoices);
      const eligibleRefundInvoices = invoicesData.filter(
        (inv) => inv.status === "approved" && parseFloat(inv.amount_paid || 0) > 0
      );
      setRefundInvoices(eligibleRefundInvoices);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "amount" ? parseFloat(value) || 0 : value,
    }));
  };

  const needsReferenceNumber = formData.mode_of_payment === "CHEQUE" || formData.mode_of_payment === "LPO";

  const getPendingAmountForInvoice = (invoice) => {
    const total = parseFloat(invoice?.total || 0);
    const paid = parseFloat(invoice?.amount_paid || 0);
    return Math.max(0, total - paid);
  };

  const getPaymentInvoiceOptionLabel = (invoice) => {
    const pendingAmount = getPendingAmountForInvoice(invoice);
    return `${invoice.invoice_number} - ${invoice.customer_name} (${invoice.currency} ${pendingAmount.toFixed(2)} pending)`;
  };

  const getVendorPaymentOptionLabel = (vendor) =>
    `${vendor.name} - due ${Number(vendor.payable_balance || 0).toFixed(2)}`;

  const getVendorRefundOptionLabel = (vendor) =>
    `${vendor.name} - credit due ${Number(vendor.vendor_credit_balance || 0).toFixed(2)}`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);

    if (!formData.invoice_id || !formData.amount || !formData.payment_date) {
      setError("Invoice, amount, and payment date are required");
      setSubmitting(false);
      return;
    }

    try {
      await api.payments.create(formData);
      setShowPaymentForm(false);
      setFormData({
        invoice_id: "",
        amount: "",
        payment_date: toLocalDateInputValue(),
        notes: "",
        mode_of_payment: "CASH",
        reference_number: "",
      });
      setPaymentInvoiceQuery("");
      fetchData();
    } catch (error) {
      console.error("Failed to record payment:", error);
      setError(error.message || "Failed to record payment. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRefundInputChange = (e) => {
    const { name, value } = e.target;
    const nextValue = name === "amount" ? parseFloat(value) || 0 : value;
    setRefundForm((prev) => ({
      ...prev,
      [name]: nextValue,
    }));
    if (name === "amount") {
      setRefundAllocations((prev) =>
        rebalanceSelectedAllocations(prev, nextValue),
      );
    }
  };

  const getReferenceFromInvoice = (invoice) => {
    const raw = invoice?.services;
    let services = [];
    if (Array.isArray(raw)) services = raw;
    else if (typeof raw === "string") {
      try {
        services = JSON.parse(raw);
      } catch (_) {
        services = [];
      }
    }
    for (const line of services) {
      const extra = line?.extra_fields || {};
      const ref =
        extra.reference_number ||
        extra.booking_reference ||
        extra.pnr ||
        line?.reference_number ||
        line?.pnr;
      if (ref && String(ref).trim()) return String(ref).trim();
    }
    return "";
  };

  const getRefundableAmountForInvoice = (invoiceId) => {
    if (!invoiceId) return 0;
    return payments
      .filter((p) => p.invoice_id === invoiceId)
      .reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
  };

  const applyDefaultRefundAllocations = (refundAmount, linesSource = []) => {
    const lines = Array.isArray(linesSource) ? linesSource : [];
    const total = Number(refundAmount || 0);
    const totalWeight = lines.reduce(
      (sum, row) => sum + Number(row.original_sell_amount || 0),
      0
    );
    let running = 0;
    const defaults = lines.map((row, idx) => {
      const sell = totalWeight > 0
        ? Number(((total * Number(row.original_sell_amount || 0)) / totalWeight).toFixed(2))
        : Number((total / Math.max(lines.length, 1)).toFixed(2));
      const refundSell = idx === lines.length - 1 ? Number((total - running).toFixed(2)) : sell;
      running = Number((running + refundSell).toFixed(2));
      const ratioBase = Number(row.original_sell_amount || 0);
      const refundCost = ratioBase > 0
        ? Number(((Number(row.original_cost_amount || 0) * refundSell) / ratioBase).toFixed(2))
        : Number(refundSell.toFixed(2));
      return {
        ...row,
        selected: true,
        refund_sell_amount: Math.max(0, refundSell),
        refund_cost_amount: Math.max(0, Math.min(refundCost, Number(row.original_cost_amount || 0))),
        receive_vendor_cash: false,
        vendor_cash_received_amount: 0,
      };
    });
    setRefundAllocations(defaults);
  };

  const getMaxRefundCostForSell = (row, sellAmount) => {
    const originalSell = Number(row?.original_sell_amount || 0);
    const originalCost = Number(row?.original_cost_amount || 0);
    const sell = Math.max(0, Number(sellAmount || 0));
    if (originalSell <= 0) return Number(originalCost.toFixed(2));
    const proportional = (originalCost * sell) / originalSell;
    return Number(Math.min(originalCost, proportional).toFixed(2));
  };

  const rebalanceSelectedAllocations = (rows, targetAmount) => {
    const list = Array.isArray(rows) ? rows : [];
    const selectedRows = list.filter((row) => row.selected);
    if (selectedRows.length === 0) return list;
    const total = Number(targetAmount || 0);
    const totalWeight = selectedRows.reduce(
      (sum, row) => sum + Number(row.original_sell_amount || 0),
      0,
    );
    let running = 0;
    const selectedKeys = new Set(selectedRows.map((row) => row.line_key));
    const nextByKey = {};

    selectedRows.forEach((row, idx) => {
      const baseSell =
        totalWeight > 0
          ? Number(
              (
                (total * Number(row.original_sell_amount || 0)) /
                totalWeight
              ).toFixed(2),
            )
          : Number((total / Math.max(selectedRows.length, 1)).toFixed(2));
      const refundSell =
        idx === selectedRows.length - 1
          ? Number((total - running).toFixed(2))
          : baseSell;
      running = Number((running + refundSell).toFixed(2));
      const ratioBase = Number(row.original_sell_amount || 0);
      const refundCost =
        ratioBase > 0
          ? Number(
              (
                (Number(row.original_cost_amount || 0) * refundSell) /
                ratioBase
              ).toFixed(2),
            )
          : Number(refundSell.toFixed(2));
      nextByKey[row.line_key] = {
        refund_sell_amount: Math.max(0, refundSell),
        refund_cost_amount: Math.max(
          0,
          Math.min(refundCost, Number(row.original_cost_amount || 0)),
        ),
      };
    });

    return list.map((row) =>
      selectedKeys.has(row.line_key) ? { ...row, ...nextByKey[row.line_key] } : row,
    );
  };

  const handleRefundInvoiceSelect = async (selectedText) => {
    setRefundInvoiceQuery(selectedText);
    const selectedInvoice = refundInvoices.find(
      (inv) => getRefundInvoiceOptionLabel(inv) === selectedText
    );
    if (!selectedInvoice) return;
    setLoadingRefundPreview(true);
    try {
      const preview = await api.payments.getRefundPreview(selectedInvoice.id);
      const refundable = Number(preview?.invoice?.refundable_amount || 0);
      const autoReference = getReferenceFromInvoice(selectedInvoice);
      setRefundForm((prev) => ({
        ...prev,
        invoice_id: selectedInvoice.id,
        amount: refundable > 0 ? Number(refundable.toFixed(2)) : "",
        reference_number:
          prev.reference_number ||
          autoReference ||
          selectedInvoice.invoice_number ||
          "",
      }));
      applyDefaultRefundAllocations(refundable, preview?.lines || []);
    } catch (err) {
      setError(err.message || "Failed to load refund preview");
    } finally {
      setLoadingRefundPreview(false);
    }
  };

  useEffect(() => {
    if (!refundForm.invoice_id || !Array.isArray(refundAllocations)) return;
    const selectedTotal = refundAllocations
      .filter((row) => row.selected)
      .reduce((sum, row) => sum + Number(row.refund_sell_amount || 0), 0);
    const rounded = Number(selectedTotal.toFixed(2));
    setRefundForm((prev) => ({
      ...prev,
      amount: rounded,
    }));
  }, [refundAllocations, refundForm.invoice_id]);

  const getRefundInvoiceOptionLabel = (invoice) => {
    const refundableAmount = getRefundableAmountForInvoice(invoice.id);
    return `${invoice.invoice_number} - ${invoice.customer_name} (${invoice.currency} ${parseFloat(refundableAmount || 0).toFixed(2)} refundable)`;
  };

  const handleRefundSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    if (!refundForm.amount || refundForm.amount <= 0 || !refundForm.refund_date) {
      setError("Refund amount and refund date are required");
      setSubmitting(false);
      return;
    }
    if (!refundForm.invoice_id) {
      setError("Select an invoice");
      setSubmitting(false);
      return;
    }

    try {
      const selectedAllocations = refundAllocations
        .filter((row) => row.selected && Number(row.refund_sell_amount || 0) > 0)
        .map((row) => ({
          line_key: row.line_key,
          refund_sell_amount: Number(row.refund_sell_amount || 0),
          refund_cost_amount: Number(row.refund_cost_amount || 0),
          receive_vendor_cash: Boolean(row.receive_vendor_cash),
          vendor_cash_received_amount: Number(row.vendor_cash_received_amount || 0),
        }));
      for (const row of selectedAllocations) {
        const source = refundAllocations.find((x) => x.line_key === row.line_key);
        if (!source) continue;
        const maxCost = getMaxRefundCostForSell(source, row.refund_sell_amount);
        if (Number(row.refund_cost_amount || 0) - maxCost > 0.02) {
          setError(
            `Refund cost is too high for ${source.service_name} / ${source.vendor_name}. Max allowed is ${maxCost.toFixed(2)}.`,
          );
          setSubmitting(false);
          return;
        }
        if (
          Number(row.receive_vendor_cash) &&
          Number(row.vendor_cash_received_amount || 0) - Number(row.refund_cost_amount || 0) > 0.02
        ) {
          setError(
            `Vendor cash received cannot exceed refund cost for ${source.service_name} / ${source.vendor_name}.`,
          );
          setSubmitting(false);
          return;
        }
      }
      await api.payments.refund({
        ...refundForm,
        allocations: selectedAllocations,
      });
      setShowRefundForm(false);
      setRefundForm({
        invoice_id: "",
        reference_number: "",
        amount: "",
        refund_date: toLocalDateInputValue(),
        notes: "",
        mode_of_payment: "BANK_TRANSFER",
      });
      setRefundAllocations([]);
      setRefundInvoiceQuery("");
      fetchData();
    } catch (err) {
      console.error("Failed to record refund:", err);
      setError(err.message || "Failed to record refund. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleVendorPaymentSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    if (!vendorPaymentForm.vendor_id || !vendorPaymentForm.amount || !vendorPaymentForm.payment_date) {
      setError("Vendor, amount, and payment date are required");
      setSubmitting(false);
      return;
    }
    if (parseFloat(vendorPaymentForm.amount || 0) > selectedVendorDue) {
      setError(`Vendor payment cannot be greater than due amount (${selectedVendorDue.toFixed(2)})`);
      setSubmitting(false);
      return;
    }
    try {
      await api.payments.createVendorPayment(vendorPaymentForm);
      setShowVendorPaymentForm(false);
      setVendorPaymentQuery("");
      setVendorPaymentForm({
        vendor_id: "",
        amount: "",
        payment_date: toLocalDateInputValue(),
        mode_of_payment: "BANK_TRANSFER",
        reference_number: "",
        notes: "",
        currency: "OMR",
      });
      fetchData();
    } catch (err) {
      setError(err.message || "Failed to record vendor payment");
    } finally {
      setSubmitting(false);
    }
  };

  const handleVendorRefundSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    if (!vendorRefundForm.vendor_id || !vendorRefundForm.amount || !vendorRefundForm.payment_date) {
      setError("Vendor, amount, and received date are required");
      setSubmitting(false);
      return;
    }
    if (parseFloat(vendorRefundForm.amount || 0) > selectedVendorCredit) {
      setError(`Vendor refund cannot be greater than credit due (${selectedVendorCredit.toFixed(2)})`);
      setSubmitting(false);
      return;
    }
    try {
      await api.payments.createVendorRefund(vendorRefundForm);
      setShowVendorRefundForm(false);
      setVendorRefundQuery("");
      setVendorRefundForm({
        vendor_id: "",
        amount: "",
        payment_date: toLocalDateInputValue(),
        mode_of_payment: "BANK_TRANSFER",
        reference_number: "",
        notes: "",
        currency: "OMR",
      });
      fetchData();
    } catch (err) {
      setError(err.message || "Failed to record vendor refund");
    } finally {
      setSubmitting(false);
    }
  };

  const getTotalPayments = () => {
    return payments.reduce(
      (sum, payment) => sum + parseFloat(payment.amount || 0),
      0
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-heading font-semibold text-secondary">
          Payments
        </h1>
        <div className="flex items-center gap-2">
          {canRecordVendorSettlement && (
            <button
              onClick={() => setShowVendorRefundForm(true)}
              className="border border-stone-300 text-stone-700 font-medium rounded-lg px-4 py-2 hover:bg-stone-50 transition-all"
            >
              Vendor Refund Received
            </button>
          )}
          {canRecordVendorSettlement && (
            <button
              onClick={() => setShowVendorPaymentForm(true)}
              className="border border-stone-300 text-stone-700 font-medium rounded-lg px-4 py-2 hover:bg-stone-50 transition-all"
            >
              Vendor Payment
            </button>
          )}
          {canRecordRefund && (
            <button
              onClick={() => setShowRefundForm(true)}
              className="border border-stone-300 text-stone-700 font-medium rounded-lg px-4 py-2 hover:bg-stone-50 transition-all"
            >
              Record Refund
            </button>
          )}
          {canRecordPayment && (
            <button
              onClick={() => setShowPaymentForm(true)}
              className="bg-primary hover:bg-primary/90 text-white font-medium rounded-lg px-4 py-2 shadow-sm hover:shadow-md transition-all flex items-center gap-2"
            >
              <Plus size={20} />
              Record Payment
            </button>
          )}
          {showTicketAction && (
            <button
              onClick={() =>
                navigate(
                  `/workflow-tickets?source_type=payment&source_id=payments-page&to_role=finance&title=Payment%20collection%20required&description=Please%20record%20or%20verify%20payment%20in%20Payments%20module.&due_date=${tomorrowDate}`,
                )
              }
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg px-4 py-2 shadow-sm hover:shadow-md transition-all"
            >
              Generate Payment Ticket
            </button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-stone-600 mb-1">Total Payments</p>
              <p className="text-2xl font-semibold text-secondary">
                {getTotalPayments().toFixed(2)}
              </p>
            </div>
            <div className="p-3 bg-primary/10 rounded-lg">
              <DollarSign size={24} className="text-primary" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-stone-600 mb-1">Total Records</p>
              <p className="text-2xl font-semibold text-secondary">
                {payments.length}
              </p>
            </div>
            <div className="p-3 bg-emerald-100 rounded-lg">
              <Calendar size={24} className="text-emerald-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-stone-600 mb-1">Pending Invoices</p>
              <p className="text-2xl font-semibold text-secondary">
                {invoices.filter((inv) => inv.payment_status !== "paid").length}
              </p>
            </div>
            <div className="p-3 bg-amber-100 rounded-lg">
              <DollarSign size={24} className="text-amber-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Payment Form Modal */}
      {showPaymentForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-stone-200">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold text-secondary">
                  Record Payment
                </h2>
                <button
                  onClick={() => {
                    setShowPaymentForm(false);
                    setError("");
                    setPaymentInvoiceQuery("");
                  }}
                  className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
                >
                  <span className="text-2xl">&times;</span>
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                  {error}
                </div>
              )}
              <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-sm text-blue-800">
                Customer payment only records cash collected and reduces the
                customer receivable. Vendor expense/payable was already created
                when the invoice was approved, so no extra expense is created here.
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  Invoice <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={paymentInvoiceQuery}
                  onChange={(e) => {
                    const selectedText = e.target.value;
                    setPaymentInvoiceQuery(selectedText);
                    const selectedInvoice = invoices.find(
                      (inv) => getPaymentInvoiceOptionLabel(inv) === selectedText
                    );
                    if (!selectedInvoice) return;
                    const pendingAmount = getPendingAmountForInvoice(selectedInvoice);
                    setFormData((prev) => ({
                      ...prev,
                      invoice_id: selectedInvoice.id,
                      amount: pendingAmount > 0 ? Number(pendingAmount.toFixed(2)) : "",
                    }));
                  }}
                  list="payment-invoice-options"
                  placeholder="Type invoice # or customer name..."
                  required
                  className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                />
                <datalist id="payment-invoice-options">
                  {invoices
                    .filter((inv) => inv.payment_status !== "paid")
                    .map((invoice) => (
                      <option key={invoice.id} value={getPaymentInvoiceOptionLabel(invoice)} />
                    ))}
                </datalist>
                {formData.invoice_id && (
                  <p className="form-helper mt-2">
                    Amount is auto-filled from pending balance. You can adjust for partial payment.
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    Amount <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="amount"
                    value={formData.amount}
                    onChange={handleInputChange}
                    required
                    min="0"
                    step="0.01"
                    className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                    placeholder="0.00"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    Payment Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    name="payment_date"
                    value={formData.payment_date}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  Mode of payment <span className="text-red-500">*</span>
                </label>
                <select
                  name="mode_of_payment"
                  value={formData.mode_of_payment}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                >
                  {PAYMENT_MODES.map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </div>

              {needsReferenceNumber && (
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    {formData.mode_of_payment === "CHEQUE" ? "Cheque number" : "PO number"}
                  </label>
                  <input
                    type="text"
                    name="reference_number"
                    value={formData.reference_number}
                    onChange={handleInputChange}
                    placeholder={formData.mode_of_payment === "CHEQUE" ? "e.g. CHQ-001" : "e.g. PO-2026-001"}
                    className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  Notes
                </label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  rows="3"
                  className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none resize-none"
                  placeholder="Payment notes (optional)"
                />
              </div>

              <div className="flex gap-4 justify-end pt-4 border-t border-stone-200">
                <button
                  type="button"
                  onClick={() => {
                    setShowPaymentForm(false);
                    setError("");
                  }}
                  className="px-6 py-2 border border-stone-300 text-stone-700 rounded-lg hover:bg-stone-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-6 py-2 bg-primary hover:bg-primary/90 text-white font-medium rounded-lg shadow-sm hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? "Recording..." : "Record Payment"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showVendorPaymentForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-stone-200">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold text-secondary">
                  Record Vendor Payment
                </h2>
                <button
                  onClick={() => {
                    setShowVendorPaymentForm(false);
                    setError("");
                  }}
                  className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
                >
                  <span className="text-2xl">&times;</span>
                </button>
              </div>
            </div>
            <form onSubmit={handleVendorPaymentSubmit} className="p-6 space-y-5">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                  {error}
                </div>
              )}
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800">
                This records actual money paid to a vendor. It does not create a
                new expense. Vendor cost is recognized when the invoice is
                approved; this entry only reduces the vendor payable balance and
                company cash/bank balance, so the same cost is not counted twice.
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  Vendor <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={vendorPaymentQuery}
                  onChange={(e) => {
                    const selectedText = e.target.value;
                    setVendorPaymentQuery(selectedText);
                    const selectedVendor = vendors.find(
                      (v) => getVendorPaymentOptionLabel(v) === selectedText
                    );
                    setVendorPaymentForm((prev) => ({
                      ...prev,
                      vendor_id: selectedVendor?.id || "",
                    }));
                  }}
                  list="vendor-payment-options"
                  placeholder="Type vendor name..."
                  required
                  className="w-full px-4 py-2 border border-stone-300 rounded-lg"
                />
                <datalist id="vendor-payment-options">
                  {vendors.map((vendor) => (
                    <option key={vendor.id} value={getVendorPaymentOptionLabel(vendor)} />
                  ))}
                </datalist>
                <p className="form-helper mt-1">
                  Search vendor by name and select from suggestions.
                </p>
                {vendorPaymentForm.vendor_id && (
                  <div className="mt-2 flex flex-wrap items-center gap-3 text-sm">
                    <span className="text-stone-600">
                      Due to vendor:{" "}
                      <strong className="text-amber-700">
                        {selectedVendorDue.toFixed(2)}
                      </strong>
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        setVendorPaymentForm((prev) => ({
                          ...prev,
                          amount: Number(selectedVendorDue.toFixed(2)),
                        }))
                      }
                      disabled={selectedVendorDue <= 0}
                      className="text-primary hover:underline disabled:text-stone-400 disabled:no-underline"
                    >
                      Pay full due
                    </button>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    Amount <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    max={selectedVendorDue || undefined}
                    step="0.01"
                    value={vendorPaymentForm.amount}
                    onChange={(e) =>
                      setVendorPaymentForm((prev) => ({
                        ...prev,
                        amount:
                          e.target.value === ""
                            ? ""
                            : parseFloat(e.target.value),
                      }))
                    }
                    required
                    className="w-full px-4 py-2 border border-stone-300 rounded-lg"
                  />
                  {vendorPaymentForm.vendor_id && (
                    <p className="form-helper mt-1">
                      Maximum allowed: {selectedVendorDue.toFixed(2)}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    Payment Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={vendorPaymentForm.payment_date}
                    onChange={(e) =>
                      setVendorPaymentForm((prev) => ({
                        ...prev,
                        payment_date: e.target.value,
                      }))
                    }
                    required
                    className="w-full px-4 py-2 border border-stone-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    Mode
                  </label>
                  <select
                    value={vendorPaymentForm.mode_of_payment}
                    onChange={(e) =>
                      setVendorPaymentForm((prev) => ({
                        ...prev,
                        mode_of_payment: e.target.value,
                      }))
                    }
                    className="w-full px-4 py-2 border border-stone-300 rounded-lg"
                  >
                    {PAYMENT_MODES.map((m) => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  Reference number
                </label>
                <input
                  type="text"
                  value={vendorPaymentForm.reference_number}
                  onChange={(e) =>
                    setVendorPaymentForm((prev) => ({
                      ...prev,
                      reference_number: e.target.value,
                    }))
                  }
                  className="w-full px-4 py-2 border border-stone-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  Notes
                </label>
                <textarea
                  rows="3"
                  value={vendorPaymentForm.notes}
                  onChange={(e) =>
                    setVendorPaymentForm((prev) => ({
                      ...prev,
                      notes: e.target.value,
                    }))
                  }
                  className="w-full px-4 py-2 border border-stone-300 rounded-lg"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-stone-200">
                <button
                  type="button"
                  onClick={() => {
                    setShowVendorPaymentForm(false);
                    setVendorPaymentQuery("");
                  }}
                  className="px-6 py-2 border border-stone-300 text-stone-700 rounded-lg hover:bg-stone-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-6 py-2 bg-primary hover:bg-primary/90 text-white rounded-lg disabled:opacity-50"
                >
                  {submitting ? "Recording..." : "Record Vendor Payment"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showVendorRefundForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-stone-200">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold text-secondary">
                  Vendor Refund Received
                </h2>
                <button
                  onClick={() => {
                    setShowVendorRefundForm(false);
                    setError("");
                  }}
                  className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
                >
                  <span className="text-2xl">&times;</span>
                </button>
              </div>
            </div>
            <form onSubmit={handleVendorRefundSubmit} className="p-6 space-y-5">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                  {error}
                </div>
              )}
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3 text-sm text-emerald-800">
                Use this only when a vendor sends money back to the company
                after a cancellation or refund. It does not create income or
                reduce expense again; it only increases cash and clears the
                vendor credit balance.
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  Vendor <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={vendorRefundQuery}
                  onChange={(e) => {
                    const selectedText = e.target.value;
                    setVendorRefundQuery(selectedText);
                    const selectedVendor = vendors.find(
                      (v) => getVendorRefundOptionLabel(v) === selectedText
                    );
                    setVendorRefundForm((prev) => ({
                      ...prev,
                      vendor_id: selectedVendor?.id || "",
                    }));
                  }}
                  list="vendor-refund-options"
                  placeholder="Type vendor name..."
                  required
                  className="w-full px-4 py-2 border border-stone-300 rounded-lg"
                />
                <datalist id="vendor-refund-options">
                  {vendors.map((vendor) => (
                    <option key={vendor.id} value={getVendorRefundOptionLabel(vendor)} />
                  ))}
                </datalist>
                <p className="form-helper mt-1">
                  Search vendor by name and select from suggestions.
                </p>
                {vendorRefundForm.vendor_id && (
                  <div className="mt-2 flex flex-wrap items-center gap-3 text-sm">
                    <span className="text-stone-600">
                      Vendor credit due:{" "}
                      <strong className="text-emerald-700">
                        {selectedVendorCredit.toFixed(2)}
                      </strong>
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        setVendorRefundForm((prev) => ({
                          ...prev,
                          amount: Number(selectedVendorCredit.toFixed(2)),
                        }))
                      }
                      disabled={selectedVendorCredit <= 0}
                      className="text-primary hover:underline disabled:text-stone-400 disabled:no-underline"
                    >
                      Receive full credit
                    </button>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    Amount <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    max={selectedVendorCredit || undefined}
                    step="0.01"
                    value={vendorRefundForm.amount}
                    onChange={(e) =>
                      setVendorRefundForm((prev) => ({
                        ...prev,
                        amount:
                          e.target.value === ""
                            ? ""
                            : parseFloat(e.target.value),
                      }))
                    }
                    required
                    className="w-full px-4 py-2 border border-stone-300 rounded-lg"
                  />
                  {vendorRefundForm.vendor_id && (
                    <p className="form-helper mt-1">
                      Maximum allowed: {selectedVendorCredit.toFixed(2)}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    Received Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={vendorRefundForm.payment_date}
                    onChange={(e) =>
                      setVendorRefundForm((prev) => ({
                        ...prev,
                        payment_date: e.target.value,
                      }))
                    }
                    required
                    className="w-full px-4 py-2 border border-stone-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    Mode
                  </label>
                  <select
                    value={vendorRefundForm.mode_of_payment}
                    onChange={(e) =>
                      setVendorRefundForm((prev) => ({
                        ...prev,
                        mode_of_payment: e.target.value,
                      }))
                    }
                    className="w-full px-4 py-2 border border-stone-300 rounded-lg"
                  >
                    {PAYMENT_MODES.map((m) => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  Reference number
                </label>
                <input
                  type="text"
                  value={vendorRefundForm.reference_number}
                  onChange={(e) =>
                    setVendorRefundForm((prev) => ({
                      ...prev,
                      reference_number: e.target.value,
                    }))
                  }
                  className="w-full px-4 py-2 border border-stone-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  Notes
                </label>
                <textarea
                  rows="3"
                  value={vendorRefundForm.notes}
                  onChange={(e) =>
                    setVendorRefundForm((prev) => ({
                      ...prev,
                      notes: e.target.value,
                    }))
                  }
                  className="w-full px-4 py-2 border border-stone-300 rounded-lg"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-stone-200">
                <button
                  type="button"
                  onClick={() => {
                    setShowVendorRefundForm(false);
                    setVendorRefundQuery("");
                  }}
                  className="px-6 py-2 border border-stone-300 text-stone-700 rounded-lg hover:bg-stone-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-6 py-2 bg-primary hover:bg-primary/90 text-white rounded-lg disabled:opacity-50"
                >
                  {submitting ? "Recording..." : "Record Vendor Refund"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Refund Form Modal */}
      {showRefundForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-stone-200">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold text-secondary">Record Refund</h2>
                <button
                  onClick={() => {
                    setShowRefundForm(false);
                    setError("");
                    setRefundAllocations([]);
                    setRefundInvoiceQuery("");
                  }}
                  className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
                >
                  <span className="text-2xl">&times;</span>
                </button>
              </div>
            </div>

            <form onSubmit={handleRefundSubmit} className="p-6 space-y-6">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                  {error}
                </div>
              )}
              <div className="bg-purple-50 border border-purple-200 rounded-lg px-4 py-3 text-sm text-purple-800">
                One refund submission now handles customer refund, income
                reversal, vendor payable/expense reversal, and optional
                immediate vendor cash received per selected line.
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    Select invoice
                  </label>
                  <input
                    type="text"
                    value={refundInvoiceQuery}
                    onChange={(e) => handleRefundInvoiceSelect(e.target.value)}
                    list="refund-invoice-options"
                    placeholder="Type invoice # or customer name..."
                    className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                  />
                  <datalist id="refund-invoice-options">
                    {refundInvoices.map((invoice) => (
                      <option key={invoice.id} value={getRefundInvoiceOptionLabel(invoice)} />
                    ))}
                  </datalist>
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    Reference number
                  </label>
                  <input
                    type="text"
                    name="reference_number"
                    value={refundForm.reference_number}
                    onChange={handleRefundInputChange}
                    placeholder="e.g. REF-2026-001"
                    className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                  />
                  <p className="form-helper mt-1">
                    Auto-filled from invoice service details when available. You can override it.
                  </p>
                </div>
              </div>

              {refundForm.invoice_id && (
                <p className="form-helper -mt-2">
                  Refund amount is auto-filled from paid balance. You can adjust
                  for partial refund and update per-line allocations below.
                </p>
              )}

              {refundForm.invoice_id && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-stone-800">
                      Service/Vendor allocation
                    </h3>
                    {loadingRefundPreview && (
                      <span className="text-xs text-stone-500">Loading preview...</span>
                    )}
                  </div>
                  <div className="overflow-x-auto border border-stone-200 rounded-lg">
                    <table className="w-full text-sm">
                      <thead className="bg-stone-50">
                        <tr>
                          <th className="px-3 py-2 text-left">Use</th>
                          <th className="px-3 py-2 text-left">Service</th>
                          <th className="px-3 py-2 text-left">Vendor</th>
                          <th className="px-3 py-2 text-right">Original Sell</th>
                          <th className="px-3 py-2 text-right">Original Cost</th>
                          <th className="px-3 py-2 text-right">Refund Sell</th>
                          <th className="px-3 py-2 text-right">Refund Cost</th>
                          <th className="px-3 py-2 text-center">Vendor cash now</th>
                          <th className="px-3 py-2 text-right">Cash received</th>
                        </tr>
                      </thead>
                      <tbody>
                        {refundAllocations.length === 0 ? (
                          <tr>
                            <td colSpan={9} className="px-3 py-3 text-center text-stone-500">
                              Select an invoice to load service/vendor lines.
                            </td>
                          </tr>
                        ) : (
                          refundAllocations.map((row) => (
                            <tr
                              key={row.line_key}
                              className={`border-t border-stone-100 transition-opacity ${
                                row.selected ? "opacity-100" : "opacity-45 bg-stone-50"
                              }`}
                            >
                              <td className="px-3 py-2">
                                <input
                                  type="checkbox"
                                  checked={!!row.selected}
                                  onChange={(e) =>
                                    setRefundAllocations((prev) =>
                                      prev.map((x) =>
                                        x.line_key === row.line_key
                                          ? {
                                              ...x,
                                              selected: e.target.checked,
                                              receive_vendor_cash: e.target.checked
                                                ? x.receive_vendor_cash
                                                : false,
                                              vendor_cash_received_amount:
                                                e.target.checked
                                                  ? x.vendor_cash_received_amount
                                                  : 0,
                                            }
                                          : x
                                      )
                                    )
                                  }
                                />
                                <p className="text-[10px] text-stone-500 mt-1">Include</p>
                              </td>
                              <td className="px-3 py-2">{row.service_name || "-"}</td>
                              <td className="px-3 py-2">{row.vendor_name || "-"}</td>
                              <td className="px-3 py-2 text-right">{Number(row.original_sell_amount || 0).toFixed(2)}</td>
                              <td className="px-3 py-2 text-right">{Number(row.original_cost_amount || 0).toFixed(2)}</td>
                              <td className="px-3 py-2">
                                <input
                                  type="number"
                                  min="0"
                                  max={Number(row.original_sell_amount || 0)}
                                  step="0.01"
                                  disabled={!row.selected}
                                  value={row.refund_sell_amount}
                                  onChange={(e) =>
                                    setRefundAllocations((prev) =>
                                      prev.map((x) =>
                                        x.line_key === row.line_key
                                          ? (() => {
                                              const nextSell = Math.min(
                                                Math.max(
                                                  0,
                                                  e.target.value === ""
                                                    ? 0
                                                    : parseFloat(e.target.value) || 0,
                                                ),
                                                Number(x.original_sell_amount || 0),
                                              );
                                              const maxCost = getMaxRefundCostForSell(x, nextSell);
                                              const nextCost = Math.min(
                                                Number(x.refund_cost_amount || 0),
                                                maxCost,
                                              );
                                              const nextVendorCash = Math.min(
                                                Number(x.vendor_cash_received_amount || 0),
                                                nextCost,
                                              );
                                              return {
                                                ...x,
                                                refund_sell_amount: nextSell,
                                                refund_cost_amount: nextCost,
                                                vendor_cash_received_amount: nextVendorCash,
                                              };
                                            })()
                                          : x
                                      )
                                    )
                                  }
                                  className="w-24 ml-auto block px-2 py-1 border border-stone-300 rounded disabled:bg-stone-100"
                                />
                              </td>
                              <td className="px-3 py-2">
                                <input
                                  type="number"
                                  min="0"
                                  max={getMaxRefundCostForSell(
                                    row,
                                    Number(row.refund_sell_amount || 0),
                                  )}
                                  step="0.01"
                                  disabled={!row.selected}
                                  value={row.refund_cost_amount}
                                  onChange={(e) =>
                                    setRefundAllocations((prev) =>
                                      prev.map((x) =>
                                        x.line_key === row.line_key
                                          ? (() => {
                                              const maxCost = getMaxRefundCostForSell(
                                                x,
                                                Number(x.refund_sell_amount || 0),
                                              );
                                              const nextCost = Math.min(
                                                Math.max(
                                                  0,
                                                  e.target.value === ""
                                                    ? 0
                                                    : parseFloat(e.target.value) || 0,
                                                ),
                                                maxCost,
                                              );
                                              const nextVendorCash = Math.min(
                                                Number(x.vendor_cash_received_amount || 0),
                                                nextCost,
                                              );
                                              return {
                                                ...x,
                                                refund_cost_amount: nextCost,
                                                vendor_cash_received_amount: nextVendorCash,
                                              };
                                            })()
                                          : x
                                      )
                                    )
                                  }
                                  className="w-24 ml-auto block px-2 py-1 border border-stone-300 rounded disabled:bg-stone-100"
                                />
                              </td>
                              <td className="px-3 py-2 text-center">
                                <input
                                  type="checkbox"
                                  disabled={!row.selected}
                                  checked={!!row.receive_vendor_cash}
                                  onChange={(e) =>
                                    setRefundAllocations((prev) =>
                                      prev.map((x) =>
                                        x.line_key === row.line_key
                                          ? { ...x, receive_vendor_cash: e.target.checked }
                                          : x
                                      )
                                    )
                                  }
                                />
                                <p className="text-[10px] text-stone-500 mt-1">Now?</p>
                              </td>
                              <td className="px-3 py-2">
                                <input
                                  type="number"
                                  min="0"
                                  max={Number(row.refund_cost_amount || 0)}
                                  step="0.01"
                                  disabled={!row.selected || !row.receive_vendor_cash}
                                  value={row.vendor_cash_received_amount}
                                  onChange={(e) =>
                                    setRefundAllocations((prev) =>
                                      prev.map((x) =>
                                        x.line_key === row.line_key
                                          ? {
                                              ...x,
                                              vendor_cash_received_amount:
                                                Math.min(
                                                  Math.max(
                                                    0,
                                                    e.target.value === ""
                                                      ? 0
                                                      : parseFloat(e.target.value) || 0,
                                                  ),
                                                  Number(x.refund_cost_amount || 0),
                                                ),
                                            }
                                          : x
                                      )
                                    )
                                  }
                                  className="w-24 ml-auto block px-2 py-1 border border-stone-300 rounded disabled:bg-stone-100"
                                />
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    Refund amount <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="amount"
                    value={refundForm.amount}
                    onChange={handleRefundInputChange}
                    min="0"
                    step="0.01"
                    required
                    className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                  />
                  <p className="form-helper mt-1">
                    Auto-calculated from selected vendor lines. Edit manually only if needed.
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    Refund date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    name="refund_date"
                    value={refundForm.refund_date}
                    onChange={handleRefundInputChange}
                    required
                    className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                  />
                  <p className="form-helper mt-1">
                    Accounting date used for all customer/vendor/company ledger entries.
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    Mode
                  </label>
                  <select
                    name="mode_of_payment"
                    value={refundForm.mode_of_payment}
                    onChange={handleRefundInputChange}
                    className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                  >
                    {PAYMENT_MODES.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </select>
                  <p className="form-helper mt-1">
                    How money was returned to the customer.
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  Notes
                </label>
                <textarea
                  name="notes"
                  value={refundForm.notes}
                  onChange={handleRefundInputChange}
                  rows="3"
                  className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none resize-none"
                  placeholder="Reason for refund..."
                />
                <p className="form-helper mt-1">
                  Optional reason/audit note for this refund event.
                </p>
              </div>

              <div className="flex gap-4 justify-end pt-4 border-t border-stone-200">
                <button
                  type="button"
                  onClick={() => {
                    setShowRefundForm(false);
                    setError("");
                    setRefundAllocations([]);
                    setRefundInvoiceQuery("");
                  }}
                  className="px-6 py-2 border border-stone-300 text-stone-700 rounded-lg hover:bg-stone-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-6 py-2 bg-primary hover:bg-primary/90 text-white font-medium rounded-lg shadow-sm hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? "Recording..." : "Record Refund"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-4">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <button
            onClick={() => setRecordsTab("customer")}
            className={`px-3 py-1.5 text-sm rounded-lg border ${
              recordsTab === "customer"
                ? "bg-primary text-white border-primary"
                : "bg-white text-stone-700 border-stone-300 hover:bg-stone-50"
            }`}
          >
            Customer Payments & Refunds
          </button>
          <button
            onClick={() => setRecordsTab("vendor")}
            className={`px-3 py-1.5 text-sm rounded-lg border ${
              recordsTab === "vendor"
                ? "bg-primary text-white border-primary"
                : "bg-white text-stone-700 border-stone-300 hover:bg-stone-50"
            }`}
          >
            Vendor Settlements
          </button>
        </div>
        {recordsTab === "customer" ? (
        <div className="flex items-center gap-2 mb-3">
          <button
            onClick={() => setPaymentsView("all")}
            className={`px-3 py-1.5 text-sm rounded-lg border ${
              paymentsView === "all"
                ? "bg-primary text-white border-primary"
                : "bg-white text-stone-700 border-stone-300 hover:bg-stone-50"
            }`}
          >
            All Records
          </button>
          <button
            onClick={() => setPaymentsView("recent_refunds")}
            className={`px-3 py-1.5 text-sm rounded-lg border ${
              paymentsView === "recent_refunds"
                ? "bg-red-600 text-white border-red-600"
                : "bg-white text-stone-700 border-stone-300 hover:bg-stone-50"
            }`}
          >
            Recent Refunds (30 days)
          </button>
        </div>
        ) : (
          <div className="flex flex-wrap items-center gap-2 mb-3">
            {[
              ["all", "All Settlements"],
              ["payments", "Payments to Vendor"],
              ["refunds", "Refunds Received"],
            ].map(([value, label]) => (
              <button
                key={value}
                onClick={() => setVendorSettlementsView(value)}
                className={`px-3 py-1.5 text-sm rounded-lg border ${
                  vendorSettlementsView === value
                    ? "bg-primary text-white border-primary"
                    : "bg-white text-stone-700 border-stone-300 hover:bg-stone-50"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        )}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <input
            type="text"
            placeholder={
              recordsTab === "customer"
                ? "Search by invoice #, customer, or reference..."
                : "Search by vendor, mode, reference, or notes..."
            }
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
          />
        </div>
        <p className="form-helper mt-2">
          Showing{" "}
          {recordsTab === "customer"
            ? `${filteredPayments.length} of ${payments.length}`
            : `${filteredVendorPayments.length} of ${vendorPayments.length}`}{" "}
          record(s)
        </p>
        {recordsTab === "customer" && <div className="flex flex-wrap gap-3 mt-3 text-xs text-stone-600">
          {[
            ["invoiceAmount", "Invoice Amount"],
            ["paidAmount", "Paid Amount"],
            ["balance", "Balance"],
          ].map(([key, label]) => (
            <label key={key} className="inline-flex items-center gap-1">
              <input
                type="checkbox"
                checked={visibleColumns[key]}
                onChange={(e) =>
                  setVisibleColumns((prev) => ({
                    ...prev,
                    [key]: e.target.checked,
                  }))
                }
              />
              {label}
            </label>
          ))}
        </div>}
      </div>

      {recordsTab === "vendor" && (
        <div className="bg-white rounded-xl border border-stone-200 shadow-sm overflow-hidden">
          <div className="p-4 border-b border-stone-200">
            <h2 className="text-lg font-semibold text-secondary">
              Recent Vendor Settlements
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-stone-50 border-b border-stone-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-stone-600 uppercase">Vendor</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-stone-600 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-stone-600 uppercase">Recorded By</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-stone-600 uppercase">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-stone-600 uppercase">Mode</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-stone-600 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-stone-600 uppercase">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-200">
                {paginatedVendorPayments.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-8 text-center text-stone-500">
                      {vendorPayments.length === 0
                        ? "No vendor settlements yet."
                        : "No vendor settlements match your filters."}
                    </td>
                  </tr>
                ) : paginatedVendorPayments.map((payment) => {
                  const amount = Number(payment.amount || 0);
                  const isRefundReceived = amount < 0;
                  return (
                    <tr key={payment.id} className="hover:bg-stone-50">
                      <td className="px-6 py-4 text-sm font-medium text-stone-700">
                        {payment.vendor_name}
                      </td>
                      <td className="px-6 py-4 text-sm text-stone-600">
                        {isRefundReceived ? "Refund received" : "Payment to vendor"}
                      </td>
                      <td className="px-6 py-4 text-sm text-stone-700">
                        {payment.recorded_by_name || "-"}
                        {payment.recorded_by_role ? ` (${payment.recorded_by_role})` : ""}
                      </td>
                      <td className={`px-6 py-4 text-right font-mono text-sm font-semibold ${isRefundReceived ? "text-emerald-600" : "text-red-600"}`}>
                        {payment.currency || "OMR"} {Math.abs(amount).toFixed(2)}
                      </td>
                      <td className="px-6 py-4 text-sm text-stone-600">
                        {payment.mode_of_payment || "-"}
                        {payment.reference_number ? ` (${payment.reference_number})` : ""}
                      </td>
                      <td className="px-6 py-4 text-sm text-stone-600">
                        {new Date(payment.payment_date).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 text-sm text-stone-600">
                        {payment.notes || "-"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {filteredVendorPayments.length > 0 && (
            <TablePagination
              page={vendorSettlementsPage}
              totalPages={vendorSettlementsTotalPages}
              onPageChange={setVendorSettlementsPage}
              totalItems={filteredVendorPayments.length}
              pageSize={tablePageSize}
              onPageSizeChange={(n) => {
                setTablePageSize(n);
                setVendorSettlementsPage(1);
                setPaymentsPage(1);
              }}
            />
          )}
        </div>
      )}

      {/* Payments List */}
      {recordsTab === "customer" && <div className="bg-white rounded-xl border border-stone-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-stone-50 border-b border-stone-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-stone-600 uppercase tracking-wider">
                  Invoice #
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-stone-600 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-stone-600 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-stone-600 uppercase tracking-wider">
                  Amount
                </th>
                {visibleColumns.invoiceAmount && (
                  <th className="px-6 py-3 text-right text-xs font-medium text-stone-600 uppercase tracking-wider">
                    Invoice Amount
                  </th>
                )}
                {visibleColumns.paidAmount && (
                  <th className="px-6 py-3 text-right text-xs font-medium text-stone-600 uppercase tracking-wider">
                    Paid Amount
                  </th>
                )}
                {visibleColumns.balance && (
                  <th className="px-6 py-3 text-right text-xs font-medium text-stone-600 uppercase tracking-wider">
                    Balance
                  </th>
                )}
                <th className="px-6 py-3 text-left text-xs font-medium text-stone-600 uppercase tracking-wider">
                  Mode
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-stone-600 uppercase tracking-wider">
                  Payment Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-stone-600 uppercase tracking-wider">
                  Recorded By
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-stone-600 uppercase tracking-wider">
                  Notes
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-200">
              {paginatedPayments.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-6 py-8 text-center text-stone-500">
                    {payments.length === 0 ? "No payments recorded yet" : "No payments match your search."}
                  </td>
                </tr>
              ) : (
                paginatedPayments.map((payment) => (
                  <tr
                    key={payment.id}
                    className="hover:bg-stone-50 transition-colors"
                  >
                    <td className="px-6 py-4 font-mono text-sm font-medium text-secondary">
                      {payment.invoice_number || "N/A"}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {(payment.transaction_type || "payment") === "refund" ? (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-700">
                          Refund
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-emerald-100 text-emerald-700">
                          Payment
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-stone-700">
                      {payment.customer_name || "N/A"}
                    </td>
                    <td className={`px-6 py-4 text-right font-mono text-sm font-semibold ${(payment.transaction_type || "payment") === "refund" ? "text-red-600" : "text-secondary"}`}>
                      {payment.currency || "OMR"}{" "}
                      {parseFloat(payment.amount).toFixed(2)}
                    </td>
                    {visibleColumns.invoiceAmount && (
                      <td className="px-6 py-4 text-right font-mono text-sm text-stone-700">
                        {payment.currency || "OMR"}{" "}
                        {parseFloat(payment.invoice_amount || 0).toFixed(2)}
                      </td>
                    )}
                    {visibleColumns.paidAmount && (
                      <td className="px-6 py-4 text-right font-mono text-sm text-stone-700">
                        {payment.currency || "OMR"}{" "}
                        {parseFloat(payment.amount_paid || 0).toFixed(2)}
                      </td>
                    )}
                    {visibleColumns.balance && (
                      <td className="px-6 py-4 text-right font-mono text-sm text-stone-700">
                        {payment.currency || "OMR"}{" "}
                        {parseFloat(payment.balance || 0).toFixed(2)}
                      </td>
                    )}
                    <td className="px-6 py-4 text-sm text-stone-600">
                      {payment.mode_of_payment ? payment.mode_of_payment.replace(/_/g, " ") : "-"}
                      {payment.reference_number ? ` (${payment.reference_number})` : ""}
                    </td>
                    <td className="px-6 py-4 text-sm text-stone-600">
                      {new Date(payment.payment_date).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-sm text-stone-700">
                      {payment.recorded_by_name || "-"}
                      {payment.recorded_by_role ? ` (${payment.recorded_by_role})` : ""}
                    </td>
                    <td className="px-6 py-4 text-sm text-stone-600">
                      {payment.notes || "-"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {filteredPayments.length > 0 && (
          <TablePagination
            page={paymentsPage}
            totalPages={paymentsTotalPages}
            onPageChange={setPaymentsPage}
            totalItems={filteredPayments.length}
            pageSize={tablePageSize}
            onPageSizeChange={(n) => {
              setTablePageSize(n);
              setPaymentsPage(1);
              setVendorSettlementsPage(1);
            }}
          />
        )}
      </div>}
    </div>
  );
}
